"use client";

// Coveo result data exposes arbitrary external image URLs that are not in the
// Next.js `images.remotePatterns` whitelist; using <Image> would silently drop
// them. Sticking with native <img> here is intentional, so disable the rule.
/* eslint-disable @next/next/no-img-element */

import { type JSX } from "react";
import {
  AtomicResultDate,
  AtomicResultImage,
  AtomicResultLink,
  AtomicResultMultiValueText,
  AtomicResultText,
} from "@coveo/atomic-react";
import type { Result } from "@coveo/headless";
import { useTranslations } from "next-intl";
import { useSearchEngine } from "../../../hooks/useSearchEngine";
import { type Density, type Display, type ImageSize } from "types/Search";
import { type SearchResultsProps } from "./search-results.props";
import { SearchResultsShell } from "./SearchResultsShell";
import fallbackImage from "src/assets/images/fallback-image.jpg";

// ---------------------------------------------------------------------------
// Shared parsing
// ---------------------------------------------------------------------------

interface ListConfig {
  display: Display;
  density: Density;
  imageSize: ImageSize;
}

const VALID_DENSITY = new Set<Density>(["comfortable", "normal", "compact"]);
const VALID_DISPLAY = new Set<Display>(["list", "grid", "table"]);
const VALID_IMAGE_SIZE = new Set<ImageSize>(["large", "small", "icon", "none"]);

const parseDensity = (value: string | undefined): Density =>
  value && VALID_DENSITY.has(value as Density)
    ? (value as Density)
    : "comfortable";

const parseDisplay = (value: string | undefined, fallback: Display): Display =>
  value && VALID_DISPLAY.has(value as Display) ? (value as Display) : fallback;

const parseImageSize = (value: string | undefined): ImageSize =>
  value && VALID_IMAGE_SIZE.has(value as ImageSize)
    ? (value as ImageSize)
    : "large";

const readListConfig = (
  params: SearchResultsProps["params"],
  defaultDisplay: Display = "list",
): ListConfig => ({
  display: parseDisplay(params?.Display, defaultDisplay),
  density: parseDensity(params?.Density),
  imageSize: parseImageSize(params?.ImageSize),
});

const isString = (value: unknown): value is string => typeof value === "string";
const getStringRaw = (result: Result, field: string): string | undefined => {
  const v = result?.raw?.[field];
  return isString(v) ? v : undefined;
};

const getImageURL = (result: Result, field: string): string | undefined => {
  const v = result?.raw?.[field];
  return isString(v) ? v : fallbackImage.src;
};

// ---------------------------------------------------------------------------
// Shared style fragments
// ---------------------------------------------------------------------------

const BASE_STYLES = `
  atomic-result-image img {
    object-fit: cover !important;
  }
  .itemlink a { text-decoration: none; }
  .itemlink a:hover { text-decoration: none; }
  :host::before { display: none !important; }
  :host { display: block; margin-bottom: 32px; }
  atomic-result::before { height: 0 !important; }
  .titlefield atomic-result-text { color: #006bd3; }
  .descriptionfield atomic-result-text { color: #555; }
  .imageWrapper { width: 100%; }
  @media (min-width: 1024px) {
    .imageWrapper { width: 240px; }
  }
  .descriptionfield {
    font-size: 16px;
    line-height: 1.5rem;
  }
`;

const responsiveColumnStyles = (wrapperClass: string) => `
  @media (max-width: 1024px) {
    .${wrapperClass} { flex-direction: column; }
  }
`;

const CARD_TAG_STYLES = `
  .cardTag {
    background-color: #f1f1f1;
    border-radius: 4px;
    color: #616365;
    font-family: 'Open Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    margin-bottom: 16px;
    text-transform: uppercase;
  }
  atomic-result-link a { color: inherit !important; }
`;

const GRID_LIST_STYLES = `
  atomic-result-list::part(result-list) {
    display: grid !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    grid-auto-rows: 1fr !important;
    gap: 1.5rem !important;
    row-gap: 1.5rem !important;
  }
  atomic-result-list::part(result-list-grid-clickable-container) {
    background-color: #ffffff;
    box-shadow: 0 2px 8px 1px rgba(0, 0, 0, 0.25);
    box-sizing: border-box !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
  }
  atomic-result-list::part(result-list-grid-clickable-container):hover {
    box-shadow: 0 2px 8px 1px rgba(0, 0, 0, 0.5);
  }
  @media (max-width: 900px) {
    atomic-result-list::part(result-list) {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
  }
  @media (max-width: 600px) {
    atomic-result-list::part(result-list) {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }
  @media (max-width: 767px) {
    atomic-result-list::part(result-list-grid-clickable-container) {
      padding: 16px;
      border-radius: 16px;
      margin-bottom: 16px;
    }
    atomic-result-list::part(result-list-grid-clickable-container)::before {
      display: none;
      margin: 0;
    }
  }
`;

// ---------------------------------------------------------------------------
// Common shell helper — extracts the boilerplate every variant shares
// ---------------------------------------------------------------------------

interface UseVariantShellArgs {
  props: SearchResultsProps;
  defaultDisplay?: Display;
  containerClassName: string;
  dataRole: string;
  ariaLabel?: string;
  template: (result: Result) => JSX.Element;
  resultListStyles?: string;
  resultListWrapperClassName?: string;
}

// Named with the `use` prefix so React's rules-of-hooks lint accepts it as a
// custom hook (it calls `useSearchEngine` internally). It returns JSX, which
// is unusual for a hook but keeps each variant export down to a single
// `return useVariantShell({...})` call.
const useVariantShell = ({
  props,
  defaultDisplay = "list",
  containerClassName,
  dataRole,
  ariaLabel,
  template,
  resultListStyles,
  resultListWrapperClassName,
}: UseVariantShellArgs): JSX.Element => {
  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;
  const id = params?.RenderingIdentifier;
  const searchEngine = useSearchEngine(params?.SearchScope);
  const config = readListConfig(params, defaultDisplay);
  const resultsNotFoundText = fields?.ResultsNotFoundText?.value ?? "";

  return (
    <SearchResultsShell
      isPageEditing={isPageEditing}
      searchEngine={searchEngine}
      id={id}
      containerClassName={containerClassName}
      dataRole={dataRole}
      ariaLabel={ariaLabel}
      display={config.display}
      density={config.density}
      imageSize={config.imageSize}
      template={template}
      resultsNotFoundText={resultsNotFoundText}
      resultListStyles={resultListStyles}
      resultListWrapperClassName={resultListWrapperClassName}
    />
  );
};

const baseSearchResultsClass = (params: SearchResultsProps["params"]) =>
  `search-results py-[32px] lg:px-[var(--spacing-sixteen)] ${params?.styles ?? ""}`;

// ---------------------------------------------------------------------------
// Variant: Default (generic page result)
// ---------------------------------------------------------------------------

const DEFAULT_STYLES = `
  ${BASE_STYLES}
  ${responsiveColumnStyles("resultWapper")}
  @media (min-width: 1024px) {
    .imageWrapper {  height: 180px; }
  }
`;

const renderDefaultTemplate = (result: Result): JSX.Element => {
  const ogImage = getImageURL(result, "page_og_image");
  return (
    <div className="w-full mb-5 bg-gray-50" aria-label="item">
      <style>{DEFAULT_STYLES}</style>
      <AtomicResultLink className="block no-underline hover:no-underline itemlink">
        <div className="resultWapper bg-white rounded-lg border border-gray-200 overflow-hidden w-full flex min-h-[160px] hover:border-gray-300 transition-colors cursor-pointer">
          {ogImage && (
            <div className="imageWrapper bg-gray-200 flex-shrink-0 relative overflow-hidden">
              <img
                alt="item"
                src={ogImage}
                className="w-full h-full object-fill relative lg:absolute inset-0"
                loading="lazy"
              />
            </div>
          )}
          <div className="p-6 flex-1 flex flex-col justify-between">
            <div>
              <h2 className="titlefield text-2xl font-medium mb-3 leading-[26.4px] tracking-[1px] no-underline">
                <AtomicResultText field="page_og_title" />
              </h2>
              <p className="descriptionfield text-gray-600 mb-4 line-clamp-3 no-underline">
                <AtomicResultText field="page_og_description" />
              </p>
            </div>
          </div>
        </div>
      </AtomicResultLink>
    </div>
  );
};

export const Default = (props: SearchResultsProps): JSX.Element => {
  console.log("---->[SearchResults:Default] Component", props.fields);
  return useVariantShell({
    props,
    containerClassName: baseSearchResultsClass(props.params),
    dataRole: "search-results-component",
    ariaLabel: "Search Results",
    template: renderDefaultTemplate,
  });
};

// ---------------------------------------------------------------------------
// Variant: Articles
// ---------------------------------------------------------------------------

const ARTICLES_STYLES = `
  ${BASE_STYLES}
  .authorfield atomic-result-multi-value-text slot { color: #616365; }
  .publishedyearfield atomic-result-date { color: #616365; }
  ${responsiveColumnStyles("articleWapper")}
`;

const renderArticlesTemplate = (result: Result): JSX.Element => {
  const ogImage = getImageURL(result, "page_og_image");
  return (
    <div className="w-full mb-5 bg-gray-50" aria-label="item">
      <style>{ARTICLES_STYLES}</style>
      <AtomicResultLink className="block no-underline hover:no-underline itemlink">
        <div className="articleWapper bg-white rounded-lg border border-gray-200 overflow-hidden w-full flex min-h-[160px] hover:border-gray-300 transition-colors cursor-pointer">
          <div className="imageWrapper bg-gray-200 flex-shrink-0 relative overflow-hidden">
            {ogImage && (
              <img
                alt="item"
                src={ogImage}
                className="w-full h-full object-fill relative lg:absolute inset-0"
                loading="lazy"
              />
            )}
          </div>
          <div className="p-6 flex-1 flex flex-col justify-between">
            <div>
              <h2 className="titlefield text-2xl font-medium mb-3 leading-[26.4px] tracking-[1px] no-underline">
                <AtomicResultText field="title" />
              </h2>
              <p className="authorfield text-dark-gray text-xl lg:text-base font-bold mb-4 leading-[20px] lg:leading-[22.8571px] line-clamp-3 no-underline">
                <AtomicResultMultiValueText field="article_author" />
              </p>
              <p className="descriptionfield text-gray-600 mb-4 line-clamp-3 no-underline">
                <AtomicResultText
                  field={
                    result?.raw?.page_og_description
                      ? "page_og_description"
                      : "excerpt"
                  }
                />
              </p>
            </div>
            <div className="publishedyearfield text-sm text-dark-gray font-bold no-underline">
              <AtomicResultDate
                field="page_publication_date"
                format="MMM DD, YYYY"
              />
            </div>
          </div>
        </div>
      </AtomicResultLink>
    </div>
  );
};

export const Articles = (props: SearchResultsProps): JSX.Element => {
  console.log("---->[SearchResults:Articles] Component", props.fields);
  return useVariantShell({
    props,
    containerClassName: baseSearchResultsClass(props.params),
    dataRole: "search-results-articles-component",
    ariaLabel: "Articles",
    template: renderArticlesTemplate,
  });
};

// ---------------------------------------------------------------------------
// Variant: Events
// ---------------------------------------------------------------------------

const EVENTS_STYLES = `
  atomic-result-image img { object-fit: cover !important; }
  .itemlink a { text-decoration: none; }
  .itemlink a:hover { text-decoration: none; }
  :host::before { display: none !important; }
  :host { display: block; margin-bottom: 32px; }
  .titlefield atomic-result-multi-value-text::part(result-multi-value-text-value) { color: #006bd3; }
  .descriptionfield atomic-result-text { color: #555; }
  .datetimestaticfield atomic-result-text { color: #616365; }
  .timezonefield atomic-result-multi-value-text::part(result-multi-value-text-value) { color: #616365; }
  .locationfield atomic-result-multi-value-text::part(result-multi-value-text-value) { color: #616365; }
  .imageWrapper { width: 100%; }
  @media (min-width: 1024px) {
    .imageWrapper { width: 240px; }
  }
  ${responsiveColumnStyles("eventWapper")}
`;

const renderEventsTemplate = (result: Result): JSX.Element => {
  const ogImage = getImageURL(result, "page_og_image");
  return (
    <div className="w-full mb-5 bg-gray-50">
      <style>{EVENTS_STYLES}</style>
      <AtomicResultLink className="block no-underline hover:no-underline itemlink">
        <div className="eventWapper bg-white rounded-lg border border-gray-200 overflow-hidden w-full flex min-h-[160px] hover:border-gray-300 transition-colors cursor-pointer">
          {ogImage && (
            <div className="imageWrapper bg-gray-200 flex-shrink-0 relative overflow-hidden">
              <img
                alt="item"
                src={ogImage}
                className="w-full h-full object-fill relative lg:absolute inset-0"
                loading="lazy"
              />
            </div>
          )}
          <div className="p-6 flex-1 flex flex-col justify-between">
            <div>
              <h2 className="titlefield text-ecolab-blue text-2xl font-medium mb-3 leading-[26.4px] tracking-[1px] no-underline">
                <AtomicResultMultiValueText field="event_title" />
              </h2>
              <h3 className="text-md font-bold mb-3 text-gray-900 leading-tight no-underline">
                <AtomicResultMultiValueText field="event_subtitle" />
              </h3>
              <p className="descriptionfield text-gray-600 mb-4 line-clamp-3 no-underline">
                <AtomicResultText
                  field={
                    result?.raw?.page_og_description
                      ? "page_og_description"
                      : "excerpt"
                  }
                />
              </p>
            </div>
            <div className="text-sm text-gray-500 font-medium no-underline">
              <p className="datetimestaticfield font-bold text-gray-600 text-sm mb-1 leading-[20px] line-clamp-3 no-underline">
                <AtomicResultDate
                  field="event_start"
                  format="MMMM DD, YYYY | hh:mm A"
                />
              </p>
              <p className="timezonefield font-medium text-sm mb-1 leading-[20px] line-clamp-3 no-underline">
                <AtomicResultMultiValueText field="event_timezone" />
              </p>
              <p className="locationfield font-bold text-gray-600 text-sm mb-0 leading-[20px] line-clamp-2 no-underline">
                <AtomicResultMultiValueText field="event_location" />
              </p>
            </div>
          </div>
        </div>
      </AtomicResultLink>
    </div>
  );
};

export const Events = (props: SearchResultsProps): JSX.Element => {
  console.log("---->[SearchResults:Events] Component", props.fields);
  return useVariantShell({
    props,
    containerClassName: baseSearchResultsClass(props.params),
    dataRole: "search-results-events-component",
    template: renderEventsTemplate,
  });
};

// ---------------------------------------------------------------------------
// Variant: News
// ---------------------------------------------------------------------------

const NEWS_STYLES = `
  ${BASE_STYLES.replace(
    ".descriptionfield atomic-result-text { color: #555; }",
    ".descriptionfield atomic-result-text { color: #555; } .publishedyearfield atomic-result-date { color: #616365; text-transform: uppercase; }",
  )}
  ${responsiveColumnStyles("newsWapper")}
`;

const renderNewsTemplate = (result: Result): JSX.Element => {
  const ogImage = getImageURL(result, "page_og_image");
  const ogTitle = getStringRaw(result, "page_og_title");
  const ogDescription = getStringRaw(result, "page_og_description");
  return (
    <div className="w-full mb-5 bg-gray-50">
      <style>{NEWS_STYLES}</style>
      <AtomicResultLink className="block no-underline hover:no-underline itemlink">
        <div className="newsWapper bg-white rounded-lg border border-gray-200 overflow-hidden w-full flex min-h-[160px] hover:border-gray-300 transition-colors cursor-pointer">
          {ogImage && (
            <div className="imageWrapper bg-gray-200 flex-shrink-0 relative overflow-hidden">
              <img
                alt="item"
                src={ogImage}
                className="w-full h-full object-fill relative lg:absolute inset-0"
                loading="lazy"
              />
            </div>
          )}
          <div className="p-6 flex-1 flex flex-col justify-between">
            <div>
              <h2 className="titlefield text-2xl font-medium mb-3 leading-[26.4px] tracking-[1px] no-underline">
                <AtomicResultText field={ogTitle ? "page_og_title" : "title"} />
              </h2>
              <p className="descriptionfield text-gray-600 mb-4 line-clamp-3 no-underline">
                <AtomicResultText
                  field={ogDescription ? "page_og_description" : "excerpt"}
                />
              </p>
            </div>
            <div className="publishedyearfield text-base text-dark-gray font-bold no-underline">
              <AtomicResultDate
                field="page_publication_date"
                format="MMMM DD, YYYY"
              />
            </div>
          </div>
        </div>
      </AtomicResultLink>
    </div>
  );
};

export const News = (props: SearchResultsProps): JSX.Element => {
  console.log("---->[SearchResults:News] Component", props.fields);
  return useVariantShell({
    props,
    containerClassName: baseSearchResultsClass(props.params),
    dataRole: "search-results-news-component",
    template: renderNewsTemplate,
  });
};

// ---------------------------------------------------------------------------
// Variant: Locations
// ---------------------------------------------------------------------------

const LOCATIONS_STYLES = `
  ${BASE_STYLES}
  .loation-region atomic-result-date { color: #616365; text-transform: uppercase; }
  .show-more { color: #006bd3; }
  :host .result-root.with-sections { display: flex !important; }
  @media not all and (width >= 1024px) {
    :host .result-root.with-sections.display-list.density-normal atomic-result-section-excerpt,
    :host .result-root.with-sections.display-list.density-normal atomic-product-section-description {
      max-height: 3.5rem !important;
    }
  }
  ${responsiveColumnStyles("locationWapper")}
`;

const renderLocationsTemplate = (result: Result): JSX.Element => {
  const ogImage = getImageURL(result, "page_og_image");
  const ogTitle = getStringRaw(result, "page_og_title");
  const ogDescription = getStringRaw(result, "page_og_description");
  return (
    <div className="w-full mb-5 bg-gray-50">
      <style>{LOCATIONS_STYLES}</style>
      <AtomicResultLink className="block no-underline hover:no-underline itemlink">
        <div className="locationWapper bg-white rounded-lg border border-gray-200 overflow-hidden w-full flex min-h-[160px] hover:border-gray-300 transition-colors cursor-pointer">
          {ogImage && (
            <div className="imageWrapper bg-gray-200 flex-shrink-0 relative overflow-hidden">
              <img
                alt="item"
                src={ogImage}
                className="w-full h-full object-fill relative lg:absolute inset-0"
                loading="lazy"
              />
            </div>
          )}
          <div className="p-6 flex-1 flex flex-col justify-between">
            <div>
              <h2 className="titlefield text-2xl font-medium mb-3 leading-[26.4px] tracking-[1px] no-underline">
                <AtomicResultText field={ogTitle ? "page_og_title" : "title"} />
              </h2>
              <p className="descriptionfield text-gray-600 mb-4 line-clamp-3 no-underline">
                <AtomicResultText
                  field={ogDescription ? "page_og_description" : "excerpt"}
                />
              </p>
            </div>
          </div>
        </div>
      </AtomicResultLink>
    </div>
  );
};

export const Locations = (props: SearchResultsProps): JSX.Element => {
  console.log("---->[SearchResults:Locations] Component", props.fields);
  return useVariantShell({
    props,
    containerClassName: baseSearchResultsClass(props.params),
    dataRole: "search-results-location-component",
    template: renderLocationsTemplate,
  });
};

// ---------------------------------------------------------------------------
// Variant: Stories
// ---------------------------------------------------------------------------

const STORIES_STYLES = `
  ${BASE_STYLES}
  .publishedyearfield atomic-result-date { color: #616365; text-transform: uppercase; }
  ${responsiveColumnStyles("storiesWapper")}
`;

const renderStoriesTemplate = (result: Result): JSX.Element => {
  const ogImage = getImageURL(result, "page_og_image");
  const ogTitle = getStringRaw(result, "page_og_title");
  const ogDescription = getStringRaw(result, "page_og_description");
  return (
    <div className="w-full mb-5 bg-gray-50">
      <style>{STORIES_STYLES}</style>
      <AtomicResultLink className="block no-underline hover:no-underline itemlink">
        <div className="storiesWapper bg-white rounded-lg border border-gray-200 overflow-hidden w-full flex min-h-[160px] hover:border-gray-300 transition-colors cursor-pointer">
          {ogImage && (
            <div className="imageWrapper bg-gray-200 flex-shrink-0 relative overflow-hidden">
              <img
                alt="item"
                src={ogImage}
                className="w-full h-full object-fill relative lg:absolute inset-0"
                loading="lazy"
              />
            </div>
          )}
          <div className="p-6 flex-1 flex flex-col justify-between">
            <div>
              <h2 className="titlefield text-2xl font-medium mb-3 leading-[26.4px] tracking-[1px] no-underline">
                <AtomicResultText field={ogTitle ? "page_og_title" : "title"} />
              </h2>
              <p className="descriptionfield text-gray-600 mb-4 line-clamp-3 no-underline">
                <AtomicResultText
                  field={ogDescription ? "page_og_description" : "excerpt"}
                />
              </p>
            </div>
            <div className="publishedyearfield text-base text-dark-gray font-bold leading-[20px] lg:leading-[22.8571px] no-underline">
              <AtomicResultDate
                field="page_publication_date"
                format="MMMM DD, YYYY"
              />
            </div>
          </div>
        </div>
      </AtomicResultLink>
    </div>
  );
};

export const Stories = (props: SearchResultsProps): JSX.Element => {
  console.log("---->[SearchResults:Stories] Component", props.fields);
  return useVariantShell({
    props,
    containerClassName: baseSearchResultsClass(props.params),
    dataRole: "search-results-stories-component",
    template: renderStoriesTemplate,
  });
};

// ---------------------------------------------------------------------------
// Variants: Solutions / Offerings (grid layout, near-identical templates)
// ---------------------------------------------------------------------------

const CATALOG_GRID_STYLES = `
  atomic-result-image img { object-fit: cover !important; }
  .itemlink a { text-decoration: none; }
  .itemlink a:hover { text-decoration: none; }
  :host::before { display: none !important; }
  :host { display: block; margin-bottom: 32px; }
  atomic-result::before { height: 0 !important; }
  .titlefield atomic-result-multi-value-text::part(result-multi-value-text-value) { color: #006bd3; }
  .descriptionfield atomic-result-text { color: #555; }
  .imageWrapper { width: 100%; }
  ${responsiveColumnStyles("eventWapper")}
  @media (min-width: 768px) {
    .titlefield { height: 103px; overflow: hidden; }
  }
  @media (min-width: 1024px) {
    .imageWrapper { width: 240px; }
    .hiddenImagefield { height: 170px; }
  }
  .imagefield { height: 170px; }
  ${CARD_TAG_STYLES}
`;

const renderCatalogGridTemplate = (result: Result): JSX.Element => {
  const offeringType = getStringRaw(result, "taxonomy_offering_type");
  return (
    <div className="bg-gray-50">
      <style>{CATALOG_GRID_STYLES}</style>
      <AtomicResultLink className="block itemlink">
        <div className="eventWapper bg-white shadow-md overflow-hidden hover:shadow-lg transition-shadow">
          {offeringType && (
            <span className="cardTag inline-block px-4 py-1">
              {offeringType}
            </span>
          )}
          <AtomicResultImage
            field="page_og_image"
            className="imagefield w-full h-48 object-cover"
            fallback={fallbackImage?.src}
          />
          <div className="mt-4 lg:py-4 flex flex-col justify-between flex-grow">
            <h2 className="titlefield text-ecolab-blue text-2xl font-medium mb-3 leading-[26.4px] tracking-[1px] no-underline">
              {result?.raw?.page_og_title ? (
                <AtomicResultMultiValueText field="page_og_title" />
              ) : (
                <AtomicResultMultiValueText field="title" />
              )}
            </h2>
            <p className="text-gray-600 descriptionfield mb-4 line-clamp-4 no-underline">
              {result?.raw?.page_og_description ? (
                <AtomicResultText field="page_og_description" />
              ) : (
                <AtomicResultText field="page_description" />
              )}
            </p>
          </div>
        </div>
      </AtomicResultLink>
    </div>
  );
};

const CATALOG_CONTAINER_CLASS = (params: SearchResultsProps["params"]) =>
  `search-results py-8 px-4 ${params?.styles ?? ""}`;

const CATALOG_LIST_WRAPPER = "!basis-[100%] lg:!basis-[25%] md:!basis-[33.33%]";

export const Solutions = (props: SearchResultsProps): JSX.Element => {
  console.log(
    "---->[SearchResults:Solutions] Component",
    props.fields,
    props.params?.SearchScope,
  );
  return useVariantShell({
    props,
    defaultDisplay: "grid",
    containerClassName: CATALOG_CONTAINER_CLASS(props.params),
    dataRole: "search-results-solutions-component",
    template: renderCatalogGridTemplate,
    resultListStyles: GRID_LIST_STYLES,
    resultListWrapperClassName: CATALOG_LIST_WRAPPER,
  });
};

export const Offerings = (props: SearchResultsProps): JSX.Element => {
  console.log("---->[SearchResults:Offerings] Component", props.fields);
  return useVariantShell({
    props,
    defaultDisplay: "grid",
    containerClassName: CATALOG_CONTAINER_CLASS(props.params),
    dataRole: "search-results-offerings-component",
    template: renderCatalogGridTemplate,
    resultListStyles: GRID_LIST_STYLES,
    resultListWrapperClassName: CATALOG_LIST_WRAPPER,
  });
};

// ---------------------------------------------------------------------------
// Variant: SDS (Safety Data Sheets)
// ---------------------------------------------------------------------------

const SDS_STYLES = `
  atomic-result::before { height: 0 !important; }
  .titlefield atomic-result-text { color: #006bd3; }
  .sds-label-text { font-weight: bold; }
  .sds-type-sds .sds-label-text { color: #006bd3; }
  .sds-type-label .sds-label-text { color: #B95104; }
  .sds-vertical-divider {
    width: 1px;
    background-color: #adafaf;
    align-self: stretch;
  }
  .sds-type {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.sds-documents {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.sds-type-sds .sds-label-text {
  color: #006bd3;
  font-weight: 700;
}

.sds-type-label .sds-label-text {
  color: #B95104;
  font-weight: 700;
}
  .sds-download-cta {
    border: 1px solid;
    color: #006bd3;
    padding: 10px 20px;
    white-space: nowrap;
  }
  .sds-download-cta:hover {
    color: #00050b;
    text-decoration: underline;
  }
  .sds-download-cta:focus {
    text-decoration: none;
  }
  :host::before { display: none !important; }
  :host { display: block; margin-bottom: 32px; }
  @media (max-width: 767px) {
    .sds-content { flex-direction: column; }
    .sds-type { justify-content: space-between; }
    .sds-mobile-fullwidth { width: 100%; }
    .sds-content { gap: 20px; }
    .sds-download-cta { width: 100%; text-align: center; }
  }
  @media (min-width: 768px) {
    .sds-region { min-width: 200px; }
    .sds-content { gap: 32px; }
  }
  atomic-result-link a:visited { color: #006bd3 !important; }
  atomic-result-link a:visited:hover { color: #00050b !important; }
`;

interface SdsRaw {
  ep_documents?: string[] | string | null;
  ep_region?: number;
}

interface SdsDocument {
  link: string;
  type: string;
  status?: string;
}

interface SdsDownloadSectionProps {
  text: string;
  link: string;
  className?: string;
  downloadLabel: string;
}

const SdsDownloadSection = ({
  text,
  link,
  className = "",
  downloadLabel,
}: SdsDownloadSectionProps): JSX.Element => (
  <div
    className={`flex items-center justify-between gap-4 sds-type ${className}`}
  >
    <span className="sds-label-text">{text}</span>
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="font-semibold rounded transition-colors whitespace-nowrap sds-download-cta"
    >
      {downloadLabel}
    </a>
  </div>
);

const stripHtmlFromSdsValue = (value: string): string =>
  value
    .replace(/<[^>]*>/g, "")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim();

const normalizeSdsLink = (value: string): string => {
  const cleaned = value
    .replace(/^"+|"+$/g, "")
    .replace(/,+$/g, "")
    .trim();

  return encodeURI(cleaned);
};

const parseSdsDocumentString = (value: string): SdsDocument | null => {
  try {
    const cleaned = value.trim();

    const linkMatch = cleaned.match(/link=(.*?)(?=,\s*type=)/i);
    const typeMatch = cleaned.match(/type=([^,}]+)/i);
    const statusMatch = cleaned.match(/status=([^,}]+)/i);

    const link = linkMatch?.[1]?.trim() ?? "";
    const type = typeMatch?.[1]?.trim() ?? "";
    const status = statusMatch?.[1]?.trim();

    if (!link || !type) {
      return null;
    }

    return {
      link: encodeURI(link),
      type,
      status,
    };
  } catch (error) {
    console.error("Failed parsing SDS document", error);
    return null;
  }
};

const getSdsDocuments = (result: Result): SdsDocument[] => {
  try {
    const raw = result.raw as unknown as SdsRaw;
    const documents = raw?.ep_documents;

    if (!documents) {
      return [];
    }

    if (Array.isArray(documents)) {
      return documents
        .map((item) =>
          typeof item === "string" ? parseSdsDocumentString(item) : null,
        )
        .filter((doc): doc is SdsDocument => doc !== null);
    }

    if (typeof documents === "string") {
      const document = parseSdsDocumentString(documents);
      return document ? [document] : [];
    }

    return [];
  } catch (error) {
    console.error("Failed processing SDS documents", error);
    return [];
  }
};

const formatDocumentType = (type: string): string => {
  const normalized = type.trim().toLowerCase();

  if (normalized === "sds") {
    return "SDS";
  }

  if (normalized === "label") {
    return "Label";
  }

  return type || "Document";
};

const getDocumentClassName = (type: string): string => {
  const normalized = type.trim().toLowerCase();

  if (normalized === "sds") {
    return "sds-type-sds";
  }

  if (normalized === "label") {
    return "sds-type-label";
  }

  return "";
};

const renderSdsTemplate = (
  result: Result,
  labels: {
    download: string;
    region: string;
    country: string;
    language: string;
  },
): JSX.Element => {
  const raw = result.raw as unknown as SdsRaw;
  const isRegion = raw.ep_region === 1;
  const documents = getSdsDocuments(result);

  return (
    <div
      className="w-full mb-5 bg-gray-50"
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      <style>{SDS_STYLES}</style>
      <div className="sdsWapper bg-white rounded-lg border border-gray-200 overflow-hidden w-full min-h-[160px]">
        <div className="p-6">
          <h2 className="titlefield text-xl font-semibold mb-6 leading-[26.4px] tracking-[1px] no-underline uppercase text-blue-600">
            <AtomicResultText field="ep_product_name" />
          </h2>
          <div className="flex items-start sds-content">
            <div className="flex flex-col gap-3 sds-region">
              <div className="flex items-start gap-2">
                <span className="text-gray-700 font-semibold whitespace-nowrap">
                  {isRegion ? labels.region : labels.country}:
                </span>
                <span className="text-gray-600">
                  <AtomicResultText field="ep_country_name" />
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-gray-700 font-semibold whitespace-nowrap">
                  {labels.language}:
                </span>
                <span className="text-gray-600">
                  <AtomicResultText field="ep_language_name" />
                </span>
              </div>
            </div>
            <div className="sds-vertical-divider" />
            <div className="flex-1 sds-mobile-fullwidth">
              <div className="sds-documents">
                {documents?.map((doc, index) => (
                  <SdsDownloadSection
                    key={`${doc.type}-${index}`}
                    text={formatDocumentType(doc.type)}
                    link={doc.link}
                    className={getDocumentClassName(doc.type)}
                    downloadLabel={labels.download}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SDS = (props: SearchResultsProps): JSX.Element => {
  console.log("---->[SearchResults:SDS] Component", props.fields);

  const t = useTranslations();
  const labels = {
    download: t.has("search-sds-download")
      ? t("search-sds-download")
      : "Download / View",
    region: t.has("search-sds-region") ? t("search-sds-region") : "Region",
    country: t.has("search-sds-country") ? t("search-sds-country") : "Country",
    language: t.has("search-sds-language")
      ? t("search-sds-language")
      : "Language",
  };

  return useVariantShell({
    props,
    containerClassName: baseSearchResultsClass(props.params),
    dataRole: "search-results-sds-component",
    template: (result) => renderSdsTemplate(result, labels),
  });
};
